import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import SelectInput from "../common/SelectInput";
import { getSpeciesList } from "../../api/projectsApi";
import { searchTaxonomy } from "../../api/taxonomyApi";
import {
  EffectiveSpeciesListItem,
  TaxonomyResponseDto,
} from "../common/ObjectTypes";
import { SpeciesOption, shouldUseWiFallback } from "./reviewActions";

type SpeciesPickerProps = {
  /** The project whose effective species list scopes the picker. */
  projectId: number;
  /** Currently selected taxon id, or null. */
  value: number | null;
  /** Label to render for the current selection (kept so the chip survives refetches). */
  valueLabel?: string | null;
  /** Called with the chosen taxon id + display label (or null when cleared). */
  onChange: (taxonId: number | null, label: string | null) => void;
  /** Disables the control while an action is committing. */
  disabled?: boolean;
  /**
   * Options to offer before the reviewer types anything (AKL-781).
   *
   * Added for the assign-species flow, which seeds the menu with the distinct
   * taxa already sitting on the ticked suggestion cards so the AI's own answer
   * can be picked rather than retyped. The first keystroke replaces them with
   * the normal effective-species-list search (or the WI fallback) — they are a
   * starting point, not a filter.
   *
   * An **offer, never a preselection**: seeding a single option does not select
   * it. `value` remains the only thing that decides what is selected.
   *
   * Treated as **candidates**: any seed that is not on the project's effective
   * species list is silently dropped, so this prop cannot widen the picker's
   * scope. See `seededMenuOptions` below for why.
   *
   * Omitted by every other caller, which keeps today's behaviour of an empty
   * menu until the reviewer types.
   */
  seededOptions?: SpeciesOption[];
  /**
   * DOM id for the combobox input, which also binds `SelectInput`'s "Species"
   * label to it (AKL-811).
   *
   * Supply it wherever the picker is one field among several in a form: without
   * it the combobox has no accessible name at all and announces as a bare
   * "combobox", while the neighbouring textarea and checkbox announce properly.
   * Omitted by callers that have not been given one yet, which keeps their
   * existing rendering byte-for-byte.
   */
  inputId?: string;
};

/**
 * Build the picker option for an effective-species-list row: label override →
 * display name → scientific name → id. Taxon names are data, not translated.
 * @param item the effective species list row
 * @returns a picker option
 */
const optionFromEffective = (
  item: EffectiveSpeciesListItem,
): SpeciesOption => ({
  value: item.taxon_id,
  label:
    item.label_override ||
    item.display_name ||
    item.scientific_name ||
    `#${item.taxon_id}`,
  scientific: item.scientific_name || "",
});

/**
 * Build the picker option for a WI taxonomy row (fallback path).
 * @param tax the taxonomy row
 * @returns a picker option
 */
const optionFromTaxonomy = (tax: TaxonomyResponseDto): SpeciesOption => ({
  value: tax.id,
  label:
    tax.common_name_english ||
    tax.scientific_name ||
    tax.species ||
    `#${tax.id}`,
  scientific: tax.scientific_name || "",
});

/**
 * Single-select taxon picker for the observation editor (§6.3). Type-ahead over
 * the project's effective species list (`getEffectiveSpeciesList`); when that
 * list is empty it falls back to searching the full Wildlife Insights taxonomy
 * (§4.6) — which is also how the WI Unknown/Blank special taxa become reachable
 * for Mark as Unknown. Emits the selected taxon id + display label.
 *
 * AKL-781: a caller may seed the menu via `seededOptions`, in which case those
 * options — not an empty menu — are what the reviewer sees before typing.
 */
const SpeciesPicker: React.FC<SpeciesPickerProps> = ({
  projectId,
  value,
  valueLabel,
  onChange,
  disabled,
  seededOptions,
  inputId,
}) => {
  const { t } = useTranslation();
  const [effective, setEffective] = useState<SpeciesOption[]>([]);
  const [useWiFallback, setUseWiFallback] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  // Cache id -> label so the selected value renders even after the list clears.
  const labelCache = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    // Reset the id→label cache when the project changes so a taxon's label
    // override from a previous project can't leak into this one.
    labelCache.current.clear();
    getSpeciesList(projectId)
      .then((resp) => {
        if (cancelled) return;
        const options = resp.items.map(optionFromEffective);
        options.forEach((o) => labelCache.current.set(o.value, o.label));
        setEffective(options);
        setUseWiFallback(shouldUseWiFallback(options.length));
      })
      .catch(() => {
        if (cancelled) return;
        // On failure, degrade to WI-taxonomy search so the reviewer can still
        // pick a taxon (the interceptor toasts the underlying error).
        setEffective([]);
        setUseWiFallback(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Debounced WI-taxonomy search (fallback path only). Stable across renders so
  // the timer persists between keystrokes; cancelled on unmount below.
  const debouncedWiSearch = useMemo(
    () =>
      debounce(
        (inputValue: string, callback: (options: SpeciesOption[]) => void) => {
          searchTaxonomy(inputValue)
            .then((results) => {
              const options = results.map(optionFromTaxonomy);
              options.forEach((o) => labelCache.current.set(o.value, o.label));
              callback(options);
            })
            .catch(() => callback([]));
        },
        300,
      ),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedWiSearch.cancel();
    };
  }, [debouncedWiSearch]);

  const loadOptions = (
    inputValue: string,
    callback: (options: SpeciesOption[]) => void,
  ) => {
    const term = inputValue.trim().toLowerCase();
    if (!useWiFallback) {
      // Filter the in-memory effective list; empty input shows the whole list.
      // This path stays synchronous and unthrottled — no API call is made.
      const filtered = term
        ? effective.filter(
            (o) =>
              o.label.toLowerCase().includes(term) ||
              o.scientific.toLowerCase().includes(term),
          )
        : effective;
      callback(filtered);
      return;
    }
    if (term.length < 3) {
      debouncedWiSearch.cancel();
      callback([]);
      return;
    }
    debouncedWiSearch(inputValue, callback);
  };

  /**
   * The seeded options actually offered, narrowed to the project's effective
   * species list (AKL-781).
   *
   * Two things happen here, and both matter.
   *
   * **Off-roster seeds are dropped** (Margaux, 2026-09-02). A ticked card's
   * taxon need not be on the project's list: the API composes a photo's
   * rejected cards from `(*rejected_aggregation.cards,
   * *rejected_aggregation.out_of_list_cards)` and tags every one `"rejected"`,
   * so an out-of-list species surfaces as a tickable card. This component's
   * contract is that the effective list scopes the picker, and seeding an
   * off-roster species would be exactly the back door the species list exists
   * to close. Nothing is blocked by dropping it — a reviewer who genuinely
   * wants that species can still type it and reach it through the normal
   * search. This can legitimately leave the seed list empty, in which case the
   * menu starts empty as it did before AKL-781; there is no fallback.
   *
   * The same species-list-scoping question is still open on AKL-777, and this
   * ruling does not transfer: that ticket is about *displaying* every model's
   * candidates, not about offering them for commit.
   *
   * **The effective-list row wins, not the card's.** Emitting the roster's own
   * option means a seeded species and the same species found by typing carry
   * an identical label — including any `label_override` — so they can never
   * read as two different species. It also keeps `labelCache` authoritative
   * for the selected-value label with no extra priming.
   *
   * Note this yields `[]` while the list is still loading, which is invisible:
   * the control is disabled until then.
   */
  const seededMenuOptions = useMemo<SpeciesOption[]>(() => {
    if (!seededOptions || seededOptions.length === 0) return [];
    const onRoster = new Map(effective.map((o) => [o.value, o]));
    const seen = new Set<number>();
    const offered: SpeciesOption[] = [];
    for (const seed of seededOptions) {
      const rosterOption = onRoster.get(seed.value);
      // Belt-and-braces: the caller already dedupes by taxon, but this is what
      // the menu renders and a repeated option is the visible symptom.
      if (!rosterOption || seen.has(seed.value)) continue;
      seen.add(seed.value);
      offered.push(rosterOption);
    }
    return offered;
  }, [seededOptions, effective]);

  const selectedOption: SpeciesOption | null =
    value == null
      ? null
      : {
          value,
          label: valueLabel || labelCache.current.get(value) || `#${value}`,
          scientific: "",
        };

  return (
    <div data-testid="species-picker" className="w-full">
      <SelectInput
        inputId={inputId}
        options={useWiFallback ? [] : effective}
        isAsync
        loadOptions={loadOptions}
        // Shown while the search input is empty; the first keystroke hands the
        // menu to `loadOptions`. Empty (the pre-AKL-781 behaviour) for every
        // caller that does not seed.
        //
        // The seeds REPLACE the empty-input menu rather than being merged into
        // the loaded list — react-select resolves its options as
        // `inputValue ? loadedOptions : defaultOptions`. That is deliberate: a
        // merge would render a ticked species that is also on the project
        // roster twice, which is the common case rather than an edge one.
        defaultOptions={seededMenuOptions}
        className="w-full bg-storm-blue rounded shadow text-left text-sm"
        isDisabled={disabled || loadingList}
        isLoading={loadingList}
        isClearable={true}
        isSearchable={true}
        isMulti={false}
        name={t("Species")}
        value={selectedOption}
        onChange={(selected: SpeciesOption | null) =>
          onChange(
            selected ? selected.value : null,
            selected ? selected.label : null,
          )
        }
        errors={""}
        searchBar={false}
        placeholder={t("Type to search species...")}
        noOptionsMessage={() =>
          useWiFallback
            ? t("Type at least 3 characters to search")
            : t("No matching species in this project")
        }
      />
    </div>
  );
};

export default SpeciesPicker;
